/* =====================================================================
   AstaLab 2.0 — SupabaseBackendAdapter

   Implementazione reale di BackendAdapter sopra Supabase (Auth +
   PostgreSQL + Row Level Security — vedi backend/migrations/ per lo
   schema e le policy). Usata quando l'ambiente ha una configurazione di
   rete valida (vedi config/env.js + backend/bootstrap.js): mai
   nell'Artifact, il cui sandbox blocca comunque ogni fetch/XHR/WebSocket
   verso un host esterno indipendentemente da cosa questo file farebbe.

   Riceve SOLO url + publishable key (mai una service_role key, mai una
   password del database): esattamente le due variabili pubbliche
   descritte in .env.example. Ogni autorizzazione reale è imposta dalle
   policy RLS lato Postgres (backend/migrations/0003_rls_policies.sql) e
   dai GRANT (0004_grants.sql), non da questo file: il client non decide
   MAI da solo se un'operazione è permessa, si limita a proporla al
   database e a propagarne l'esito (obiettivo 10: "non fidarti del client
   per autorizzazioni").
   ===================================================================== */
import { createClient, isAuthSessionMissingError } from "@supabase/supabase-js";
import { BackendAdapter, LocalAuthAttemptTracker } from "./BackendAdapter.js";
import { validatePasswordPolicy } from "../config/authPolicy.js";

function mapLobbyRpcError(error){
  if(!error) return null;
  const msg = String(error.message || error);
  if(/solo il proprietario/i.test(msg)) return msg;
  if(/non sei membro/i.test(msg)) return msg;
  if(/codice non valido/i.test(msg)) return msg;
  if(/archiviata/i.test(msg)) return msg;
  // A7-P1-2 (migration 0016): nomination-completeness rejection — già testo applicativo curato in
  // italiano dalla RPC (nominate_player), stesso passthrough di ogni altro ramo qui sopra; registrato
  // esplicitamente per coerenza con ogni altro messaggio lobby/live enumerato in questa funzione.
  if(/rosa al completo/i.test(msg)) return msg;
  return msg;
}

function mapAuthError(error){
  if(!error) return null;
  // Messaggi Supabase tradotti/riformulati in italiano per la UI, senza esporre dettagli
  // tecnici interni (stack, codici HTTP grezzi) all'utente finale.
  const msg = String(error.message || error);
  if(/already registered|already exists/i.test(msg)) return "Esiste già un account con questa email.";
  if(/invalid login credentials/i.test(msg)) return "Email o password non corrette.";
  if(/email not confirmed/i.test(msg)) return "Devi confermare la tua email prima di accedere.";
  if(/password.*(least|short|weak)/i.test(msg)) return "La password non soddisfa i requisiti minimi.";
  return msg;
}

// Round alpha.6 P0.4-A (ERROR MESSAGE CONSISTENCY): helper PURO dedicato per le mutazioni dirette
// .update()/.insert() su una tabella (updateProfile/renameAuction/archiveAuction) — mai un riuso
// deformato di mapLobbyRpcError() sopra, che è per costruzione un passthrough (ogni suo ramo
// restituisce lo stesso `msg` grezzo indipendentemente dal pattern trovato, perché i messaggi delle
// RPC di lobby/live sono già testo applicativo curato in italiano scritto dalle funzioni SQL stesse —
// mai testo PostgREST tecnico da filtrare). Le mutazioni dirette qui sotto, al contrario, possono
// restituire errori PostgREST/Postgres realmente grezzi (policy RLS/GRANT, vincoli CHECK/UNIQUE/FK,
// codici SQLSTATE) — mai da mostrare così com'è all'utente finale. Riconosce solo pattern stabili del
// vocabolario Postgres/PostgREST realmente documentato (mai un parsing fragile di un messaggio
// iper-specifico non consolidato altrove nel progetto). Fallback italiano generico, mai SQL/nomi di
// policy/dettagli di relazione/codici Postgres/testo del server anche nel ramo di default. Esportata
// per essere testabile in isolamento (tests/test_data_mutation_error_mapping.js), mai per un uso al di
// fuori del modulo adapter.
export function mapDataMutationError(error){
  const GENERIC_FALLBACK = "Si è verificato un problema durante il salvataggio. Riprova più tardi.";
  if(!error) return GENERIC_FALLBACK;
  const msg = String((error && error.message) || error || "");
  if(!msg) return GENERIC_FALLBACK;
  if(/row-level security|permission denied/i.test(msg)){
    return "Non hai i permessi per eseguire questa operazione.";
  }
  if(/violates check constraint|violates foreign key constraint|duplicate key value violates unique constraint|null value in column/i.test(msg)){
    return "I dati inseriti non sono validi.";
  }
  return GENERIC_FALLBACK;
}

export class SupabaseBackendAdapter extends BackendAdapter{
  /**
   * @param {string} supabaseUrl
   * @param {string} supabasePublishableKey
   * @param {string|null} [authRedirectUrl] - base URL pubblica per i link di redirect di Auth
   *   (es. il recupero password), letta da config/env.js (mai hardcodata qui — obiettivo 8 della
   *   revisione r1). Se assente, viene calcolata a runtime da `window.location` al momento della
   *   chiamata: mai un valore fisso "localhost:3000" congelato nel codice sorgente.
   */
  constructor(supabaseUrl, supabasePublishableKey, authRedirectUrl){
    super();
    if(!supabaseUrl || !supabasePublishableKey){
      throw new Error("SupabaseBackendAdapter richiede sempre supabaseUrl e supabasePublishableKey (mai una service_role key).");
    }
    this._client = createClient(supabaseUrl, supabasePublishableKey);
    this._authRedirectUrl = authRedirectUrl || null;
    // Round Beta-P3-3 (design D4): per-istanza, mai persistito — vedi LocalAuthAttemptTracker.
    this._localAttempts = new LocalAuthAttemptTracker();
  }

  get kind(){ return "supabase"; }

  _resetPasswordRedirectTo(){
    if(this._authRedirectUrl) return this._authRedirectUrl.replace(/\/?$/, "") + "#/reset-password";
    if(typeof window !== "undefined" && window.location){
      return window.location.origin + window.location.pathname + "#/reset-password";
    }
    return undefined;
  }

  // Round Beta-P3-3 (design D4): this._localAttempts.begin(email)/.end(email) avvolgono l'INTERA
  // chiamata — mai gli argomenti di signUp()/signInWithPassword() stessi, invariati — cosi' che il
  // tracker sia già attivo prima della chiamata di rete reale e dell'evento onAuthStateChange che
  // essa può innescare (che il SDK Supabase emette PRIMA che questa stessa promise si risolva).
  async signUp(email, password){
    this._localAttempts.begin(email);
    try{
      const policy = validatePasswordPolicy(password);
      if(!policy.ok) return { ok:false, user:null, error:policy.error, notice:null };
      const { data, error } = await this._client.auth.signUp({ email, password });
      if(error) return { ok:false, user:null, error: mapAuthError(error), notice:null };
      const user = data.user ? { id:data.user.id, email:data.user.email } : null;
      const needsConfirmation = !data.session;
      return {
        ok:true, user, error:null,
        notice: needsConfirmation ? "Controlla la tua email per confermare la registrazione prima di accedere." : null
      };
    } finally {
      this._localAttempts.end(email);
    }
  }

  async signIn(email, password){
    this._localAttempts.begin(email);
    try{
      const { data, error } = await this._client.auth.signInWithPassword({ email, password });
      if(error) return { ok:false, user:null, error: mapAuthError(error), notice:null };
      return { ok:true, user: { id:data.user.id, email:data.user.email }, error:null, notice:null };
    } finally {
      this._localAttempts.end(email);
    }
  }

  async signOut(){
    const { error } = await this._client.auth.signOut();
    return { ok:!error, error: error ? mapAuthError(error) : null };
  }

  // Round Beta-P3-4 (design D/D2, accettato): AuthSessionMissingError è l'UNICA condizione che il
  // SDK stesso tratta come autorevolmente "nessuna sessione" (vedi BackendAdapter.js per la
  // motivazione tracciata sulla sorgente installata) — ogni altro AuthError (token rifiutato dal
  // server, rete, sconosciuto) o eccezione inattesa resta un'incertezza, mai convertita in null qui:
  // il rigetto si propaga al chiamante così com'è, mai ispezionato per messaggio, mai un signOut()/
  // rimozione di sessione manuale (già di competenza del SDK stesso per il solo caso autoritativo).
  async getCurrentUser(){
    const { data, error } = await this._client.auth.getUser();
    if(error){
      if(isAuthSessionMissingError(error)) return null;
      throw error;
    }
    return data && data.user ? { id:data.user.id, email:data.user.email } : null;
  }

  async requestPasswordReset(email){
    // redirectTo deve combaciare con un pattern autorizzato nelle impostazioni Auth del progetto
    // Supabase (es. http://localhost:3000/** in sviluppo) — configurabile via env, mai hardcodato
    // (obiettivo 8 della revisione r1, vedi _resetPasswordRedirectTo() sopra).
    const { error } = await this._client.auth.resetPasswordForEmail(email, { redirectTo: this._resetPasswordRedirectTo() });
    return { ok:!error, error: error ? mapAuthError(error) : null, notice:null };
  }

  async updatePassword(newPassword){
    const policy = validatePasswordPolicy(newPassword);
    if(!policy.ok) return { ok:false, error:policy.error, notice:null };
    const { error } = await this._client.auth.updateUser({ password:newPassword });
    return { ok:!error, error: error ? mapAuthError(error) : null, notice:null };
  }

  /**
   * Revisione r1 (obiettivo 8): propaga il vero `event` di supabase-js (INITIAL_SESSION,
   * SIGNED_IN, SIGNED_OUT, PASSWORD_RECOVERY, USER_UPDATED, TOKEN_REFRESHED, ...) — la alpha.1 lo
   * scartava (`_event`), rendendo impossibile per app.js distinguere un login normale da un
   * arrivo tramite link di recupero password (PASSWORD_RECOVERY), che deve invece navigare
   * l'utente a /reset-password invece che a /dashboard.
   *
   * Round Beta-P3-3 (design D4, accettato): terzo argomento `meta` additivo — vedi la stessa
   * semantica documentata in BackendAdapter.js. Calcolato QUI, dallo stesso `user` appena derivato
   * dalla sessione riportata da supabase-js, mai un secondo canale di identità.
   */
  onAuthStateChange(callback){
    const { data } = this._client.auth.onAuthStateChange((event, session) => {
      const user = session && session.user ? { id:session.user.id, email:session.user.email } : null;
      const meta = { isLocalInteractiveAuthInFlight: !!(user && this._localAttempts.isInFlight(user.email)) };
      callback(event, user, meta);
    });
    return () => data.subscription.unsubscribe();
  }

  async getProfile(userId){
    const { data, error } = await this._client.from("profiles").select("id, display_name, created_at").eq("id", userId).maybeSingle();
    if(error || !data) return null;
    return { id:data.id, displayName:data.display_name, createdAt:data.created_at };
  }

  async updateProfile(userId, patch){
    const row = {};
    if(patch && typeof patch.displayName === "string") row.display_name = patch.displayName;
    const { error } = await this._client.from("profiles").update(row).eq("id", userId);
    return { ok:!error, error: error ? mapDataMutationError(error) : null };
  }

  async listMyAuctions(){
    // RLS (auctions_select_owner_or_member) limita già il risultato alle sole aste visibili
    // all'utente corrente: nessun filtro aggiuntivo lato client è necessario né sufficiente da solo.
    const { data: userData } = await this._client.auth.getUser();
    const myId = userData && userData.user ? userData.user.id : null;
    const { data, error } = await this._client
      .from("auctions")
      .select("id, owner_id, name, season, status, created_at, updated_at")
      .order("updated_at", { ascending:false });
    if(error || !data) return [];
    return data.map(a => this._toSummary(a, myId));
  }

  _toSummary(a, myId){
    return {
      id:a.id, name:a.name, season:a.season||null, status:a.status,
      ownerId:a.owner_id, myRole: a.owner_id===myId ? "owner" : "member",
      createdAt:a.created_at, updatedAt:a.updated_at
    };
  }

  async createAuction({name, season}){
    const { data: userData } = await this._client.auth.getUser();
    const myId = userData && userData.user ? userData.user.id : null;
    if(!myId) return { ok:false, auction:null, error:"Devi accedere prima di creare un'asta." };
    // owner_id e' impostato qui SOLO al valore letto dalla sessione Supabase autenticata
    // (auth.getUser(), mai un parametro passato dal chiamante) — e comunque, anche se questo
    // client venisse manomesso per inviare un owner_id diverso, la policy RLS
    // auctions_insert_own (WITH CHECK owner_id = auth.uid()) lo rifiuterebbe lato database.
    const { data, error } = await this._client
      .from("auctions")
      .insert({ owner_id: myId, name: String(name||"").trim(), season: season || null })
      .select()
      .single();
    if(error) return { ok:false, auction:null, error: error.message };
    return { ok:true, auction: this._toSummary(data, myId), error:null };
  }

  async renameAuction(auctionId, name){
    const { error } = await this._client.from("auctions").update({ name: String(name||"").trim() }).eq("id", auctionId);
    return { ok:!error, error: error ? mapDataMutationError(error) : null };
  }

  async archiveAuction(auctionId){
    const { error } = await this._client.from("auctions").update({ status:"archived" }).eq("id", auctionId);
    return { ok:!error, error: error ? mapDataMutationError(error) : null };
  }

  async getAuctionSummary(auctionId){
    const { data: userData } = await this._client.auth.getUser();
    const myId = userData && userData.user ? userData.user.id : null;
    const { data, error } = await this._client
      .from("auctions").select("id, owner_id, name, season, status, created_at, updated_at")
      .eq("id", auctionId).maybeSingle();
    if(error || !data) return null;
    return this._toSummary(data, myId);
  }

  async loadAuctionState(auctionId){
    const { data, error } = await this._client
      .from("auction_states").select("auction_id, revision, state_json, updated_at")
      .eq("auction_id", auctionId).maybeSingle();
    if(error || !data) return null;
    return { auctionId:data.auction_id, revision:data.revision, updatedAt:data.updated_at, state:data.state_json };
  }

  async saveAuctionState(auctionId, state, expectedRevision){
    if(expectedRevision === 0){
      // Nessuna riga precedente attesa: un INSERT che fallisce per violazione della chiave
      // primaria (auction_id già presente) significa che qualcun altro ha già salvato per
      // primo — trattato come conflitto, mai come sovrascrittura silenziosa.
      const { data, error } = await this._client
        .from("auction_states")
        .insert({ auction_id: auctionId, revision: 1, state_json: state })
        .select("revision")
        .single();
      if(error){
        if(error.code === "23505") return { ok:false, conflict:true, revision:null, error:"Un'altra scheda ha già inizializzato lo stato di questa asta." };
        return { ok:false, conflict:false, revision:null, error: error.message };
      }
      return { ok:true, conflict:false, revision:data.revision, error:null };
    }
    // Concorrenza ottimistica reale (obiettivo 8): l'UPDATE è condizionato ANCHE su
    // revision = expectedRevision lato SQL — se un'altra scheda ha già scritto una revision
    // più recente, questa condizione non seleziona alcuna riga e Supabase restituisce un
    // risultato vuoto, mai un errore silenzioso che nasconderebbe la sovrascrittura mancata.
    const { data, error } = await this._client
      .from("auction_states")
      .update({ revision: expectedRevision + 1, state_json: state })
      .eq("auction_id", auctionId)
      .eq("revision", expectedRevision)
      .select("revision")
      .maybeSingle();
    if(error) return { ok:false, conflict:false, revision:null, error: error.message };
    if(!data) return { ok:false, conflict:true, revision:null, error:"Un'altra scheda ha salvato una versione più recente di questa asta." };
    return { ok:true, conflict:false, revision:data.revision, error:null };
  }

  // ---------------- Lobby multiplayer / join-code (round alpha.4-r1) ----------------
  // Ogni operazione owner-only/di join passa da un'RPC SECURITY DEFINER (backend/migrations/
  // 0012_multiplayer_lobby_foundation.sql) — mai una scrittura diretta su auction_join_codes (RLS
  // abilitata con ZERO policy per authenticated, nessun GRANT: irraggiungibile da .from(), per
  // costruzione) né su auction_members per il join (l'identità è sempre auth.uid() letta
  // internamente dalla RPC, mai un valore che questo file potrebbe scegliere).

  async joinAuctionByCode(rawCode){
    const { data, error } = await this._client.rpc("join_auction_by_code", { p_code: rawCode });
    if(error) return { ok:false, auctionId:null, alreadyMember:false, error: mapLobbyRpcError(error) };
    return { ok:true, auctionId:data.auctionId, alreadyMember:!!data.alreadyMember, error:null };
  }

  async listAuctionMembers(auctionId){
    const { data, error } = await this._client.rpc("list_auction_members", { p_auction_id: auctionId });
    if(error) throw new Error(mapLobbyRpcError(error));
    return (data || []).map(m => ({ userId:m.userId, displayName:m.displayName, role:m.role, joinedAt:m.joinedAt }));
  }

  async rotateJoinCode(auctionId){
    const { data, error } = await this._client.rpc("rotate_auction_join_code", { p_auction_id: auctionId });
    if(error) return { ok:false, code:null, generatedAt:null, error: mapLobbyRpcError(error) };
    return { ok:true, code:data.code, generatedAt:data.generatedAt, error:null };
  }

  async revokeJoinCode(auctionId){
    const { error } = await this._client.rpc("revoke_auction_join_code", { p_auction_id: auctionId });
    return { ok:!error, error: error ? mapLobbyRpcError(error) : null };
  }

  async getJoinCodeStatus(auctionId){
    const { data, error } = await this._client.rpc("get_auction_join_code_status", { p_auction_id: auctionId });
    if(error) return { ok:false, active:false, generatedAt:null, error: mapLobbyRpcError(error) };
    return { ok:true, active:!!data.active, generatedAt:data.generatedAt || null, error:null };
  }

  // Owner-remove/self-leave restano DELETE di tabella diretti (mai un'RPC): la policy RLS
  // auction_members_delete_self_or_owner (0012) impone lato Postgres che solo l'owner possa
  // rimuovere un member altrui e che chiunque possa rimuovere SOLO la propria riga non-owner — mai
  // la riga role='owner', in nessuno dei due casi.
  async removeAuctionMember(auctionId, memberUserId){
    const { error, count } = await this._client
      .from("auction_members")
      .delete({ count:"exact" })
      .eq("auction_id", auctionId)
      .eq("user_id", memberUserId);
    if(error) return { ok:false, error: error.message };
    if(!count) return { ok:false, error:"Rimozione non riuscita: membro non trovato, oppure non hai i permessi per rimuoverlo." };
    return { ok:true, error:null };
  }

  async leaveAuction(auctionId){
    const { data: userData } = await this._client.auth.getUser();
    const myId = userData && userData.user ? userData.user.id : null;
    if(!myId) return { ok:false, error:"Devi accedere per lasciare un'asta." };
    const { error, count } = await this._client
      .from("auction_members")
      .delete({ count:"exact" })
      .eq("auction_id", auctionId)
      .eq("user_id", myId);
    if(error) return { ok:false, error: error.message };
    if(!count) return { ok:false, error:"Non hai lasciato l'asta: non risulti membro, oppure sei il proprietario (che non può abbandonare la propria asta)." };
    return { ok:true, error:null };
  }

  // ---------------- Live auction: seat/squadra (round alpha.5-r1) ----------------
  // Ogni scrittura passa da un'RPC SECURITY DEFINER (backend/migrations/
  // 0013_live_auction_foundation.sql) — mai un INSERT/UPDATE/DELETE diretto su auction_seats (RLS
  // abilitata con SELECT owner-or-member ma ZERO policy di scrittura per authenticated). La lettura
  // (listAuctionSeats) resta invece un SELECT diretto, filtrato dalla RLS: non è un segreto come
  // l'hash del join-code, coerente con LIVE_AUCTION_DESIGN_alpha5_r1.md §2.6.

  async createAuctionSeat(auctionId, label){
    const { data, error } = await this._client.rpc("create_auction_seat", { p_auction_id: auctionId, p_label: label });
    if(error) return { ok:false, seatId:null, seatNo:null, label:null, error: mapLobbyRpcError(error) };
    return { ok:true, seatId:data.seatId, seatNo:data.seatNo, label:data.label, error:null };
  }

  // BLOCKER A (audit indipendente su r2): la RPC (0013, r3) ora solleva sempre un'eccezione se non
  // riesce a eliminare esattamente una riga (mai più un `deleted:false` silenzioso con `ok:true`) —
  // ma questo client non deve MAI assumere che `data.deleted` sia sempre true solo perché non c'è
  // stato un errore di trasporto: un `deleted:false` esplicito nel payload (difesa in profondità, nel
  // caso improbabile che una versione futura della RPC lo reintroducesse) deve propagarsi come
  // `ok:false`, mai essere ignorato silenziosamente.
  async deleteAuctionSeat(seatId){
    const { data, error } = await this._client.rpc("delete_auction_seat", { p_seat_id: seatId });
    if(error) return { ok:false, error: mapLobbyRpcError(error) };
    if(data && data.deleted === false) return { ok:false, error:"Eliminazione della squadra non riuscita." };
    return { ok:true, error:null };
  }

  // BLOCKER C (audit indipendente su r1): NON referenzia mai `profiles` da qui — `profiles` è
  // self-only (`profiles_select_own`, 0003, invariata) e non deve mai essere aperta globalmente per
  // risolvere un nome di visualizzazione. Restituisce solo `assignedUserId` (mai un nome): il
  // chiamante (AuctionDetailPage.js) risolve il nome cercando quell'id nella member list già ottenuta
  // dalla RPC sicura `list_auction_members()` (0012, invariata), l'unica via autorizzata a leggere
  // display_name di membri altrui. Outsider (RLS non lo autorizza): righe zero, MAI un errore — stessa
  // semantica di una SELECT diretta filtrata da RLS (0003/questo file, mai un 404/403 esplicito per
  // costruzione), allineata al Demo Adapter (BLOCKER C, vedi ArtifactDemoBackendAdapter.js).
  async listAuctionSeats(auctionId){
    const { data, error } = await this._client
      .from("auction_seats")
      .select("id, seat_no, label, assigned_user_id")
      .eq("auction_id", auctionId)
      .order("seat_no", { ascending:true });
    if(error) throw new Error(mapLobbyRpcError(error));
    return (data || []).map(s => ({
      id: s.id, seatNo: s.seat_no, label: s.label,
      assignedUserId: s.assigned_user_id || null
    }));
  }

  async assignMemberToSeat(seatId, userId){
    const { error } = await this._client.rpc("assign_member_to_seat", { p_seat_id: seatId, p_user_id: userId });
    return { ok:!error, error: error ? mapLobbyRpcError(error) : null };
  }

  async unassignMemberFromSeat(seatId){
    const { error } = await this._client.rpc("unassign_member_from_seat", { p_seat_id: seatId });
    return { ok:!error, error: error ? mapLobbyRpcError(error) : null };
  }

  // ---------------- Live auction: lifecycle della sessione (round alpha.5-r1) ----------------

  // Estensione round alpha.5-r4-r1: seleziona anche le colonne di nomina/offerta/timer aggiunte da
  // 0014 (già presenti come colonne riservate da 0013, ora effettivamente scritte dalle RPC
  // nomination/bidding) — stessa SELECT diretta filtrata da RLS di prima, nessuna nuova RPC per la
  // sola lettura.
  async loadLiveSession(auctionId){
    const { data, error } = await this._client
      .from("auction_live_sessions")
      // A7-P1-2 (§16): closed_at aggiunto — unico timestamp persistito/autoritativo disponibile per il
      // riepilogo di completamento (mostrato solo come "chiusa il", mai rietichettato come istante di
      // completamento — quel concetto non esiste lato schema, vedi A7-P1-2D §1).
      .select("auction_id, lifecycle, revision, nomination_status, current_nomination_id, nominated_player_fantacalcio_id, nominating_seat_id, current_highest_bid, highest_bidder_seat_id, bid_seq, deadline_at, closed_at")
      .eq("auction_id", auctionId)
      .maybeSingle();
    if(error) throw new Error(mapLobbyRpcError(error));
    if(!data) return null;
    return {
      auctionId: data.auction_id, lifecycle: data.lifecycle, revision: data.revision,
      nominationStatus: data.nomination_status || null,
      currentNominationId: data.current_nomination_id || null,
      nominatedPlayerFantacalcioId: data.nominated_player_fantacalcio_id != null ? data.nominated_player_fantacalcio_id : null,
      nominatingSeatId: data.nominating_seat_id || null,
      currentHighestBid: data.current_highest_bid != null ? data.current_highest_bid : null,
      highestBidderSeatId: data.highest_bidder_seat_id || null,
      closedAt: data.closed_at || null,
      bidSeq: data.bid_seq || 0,
      deadlineAt: data.deadline_at || null
    };
  }

  async startLiveSession(auctionId, expectedRevision){
    const { data, error } = await this._client.rpc("start_live_session", { p_auction_id: auctionId, p_expected_revision: expectedRevision });
    if(error) return { ok:false, conflict:false, revision:null, lifecycle:null, error: mapLobbyRpcError(error) };
    return { ok:data.ok, conflict:!!data.conflict, revision:data.revision, lifecycle:data.lifecycle, error:data.error||null };
  }

  async pauseLiveSession(auctionId, expectedRevision){
    const { data, error } = await this._client.rpc("pause_live_session", { p_auction_id: auctionId, p_expected_revision: expectedRevision });
    if(error) return { ok:false, conflict:false, revision:null, lifecycle:null, error: mapLobbyRpcError(error) };
    return { ok:data.ok, conflict:!!data.conflict, revision:data.revision, lifecycle:data.lifecycle, error:data.error||null };
  }

  async resumeLiveSession(auctionId, expectedRevision){
    const { data, error } = await this._client.rpc("resume_live_session", { p_auction_id: auctionId, p_expected_revision: expectedRevision });
    if(error) return { ok:false, conflict:false, revision:null, lifecycle:null, error: mapLobbyRpcError(error) };
    return { ok:data.ok, conflict:!!data.conflict, revision:data.revision, lifecycle:data.lifecycle, error:data.error||null };
  }

  async closeLiveSession(auctionId, expectedRevision){
    const { data, error } = await this._client.rpc("close_live_session", { p_auction_id: auctionId, p_expected_revision: expectedRevision });
    if(error) return { ok:false, conflict:false, revision:null, lifecycle:null, error: mapLobbyRpcError(error) };
    return { ok:data.ok, conflict:!!data.conflict, revision:data.revision, lifecycle:data.lifecycle, error:data.error||null };
  }

  // ---------------- Live auction: nomina/offerta/timer (round alpha.5-r4-r1) ----------------
  // Ogni scrittura passa da un'RPC SECURITY DEFINER (backend/migrations/
  // 0014_live_auction_nomination_bidding.sql) — mai un INSERT/UPDATE/DELETE diretto su
  // auction_live_rules/auction_nominations/auction_bids (RLS abilitata con SELECT owner-or-member ma
  // ZERO policy di scrittura per authenticated). La lettura di auction_live_rules resta invece un
  // SELECT diretto, filtrato dalla RLS — stessa scelta già fatta per listAuctionSeats().

  async loadLiveRules(auctionId){
    const { data, error } = await this._client
      .from("auction_live_rules")
      .select("auction_id, opening_price, initial_timer_seconds, reset_on_bid, reset_timer_seconds, revision")
      .eq("auction_id", auctionId)
      .maybeSingle();
    if(error) throw new Error(mapLobbyRpcError(error));
    if(!data) return null;
    return {
      auctionId: data.auction_id, openingPrice: data.opening_price, initialTimerSeconds: data.initial_timer_seconds,
      resetOnBid: data.reset_on_bid, resetTimerSeconds: data.reset_timer_seconds, revision: data.revision
    };
  }

  async configureLiveRules(auctionId, rules, expectedRevision){
    const r = rules || {};
    const { data, error } = await this._client.rpc("configure_live_rules", {
      p_auction_id: auctionId, p_opening_price: r.openingPrice, p_initial_timer_seconds: r.initialTimerSeconds,
      p_reset_on_bid: r.resetOnBid, p_reset_timer_seconds: r.resetTimerSeconds, p_expected_revision: expectedRevision
    });
    if(error) return { ok:false, conflict:false, revision:null, error: mapLobbyRpcError(error) };
    return { ok:data.ok, conflict:!!data.conflict, revision:data.revision, error:data.error||null };
  }

  async nominatePlayer(auctionId, seatId, fantacalcioId, expectedRevision, commandId){
    const { data, error } = await this._client.rpc("nominate_player", {
      p_auction_id: auctionId, p_seat_id: seatId, p_fantacalcio_id: fantacalcioId,
      p_expected_revision: expectedRevision, p_command_id: commandId
    });
    if(error) return { ok:false, conflict:false, revision:null, nominationId:null, nominationSeq:null, openingPrice:null, deadlineAt:null, error: mapLobbyRpcError(error) };
    return {
      ok:data.ok, conflict:!!data.conflict, revision:data.revision, nominationId:data.nominationId,
      nominationSeq:data.nominationSeq, openingPrice:data.openingPrice, deadlineAt:data.deadlineAt, error:data.error||null
    };
  }

  async placeBid(auctionId, nominationId, seatId, amount, expectedBidSeq, commandId){
    const { data, error } = await this._client.rpc("place_bid", {
      p_auction_id: auctionId, p_nomination_id: nominationId, p_seat_id: seatId, p_amount: amount,
      p_expected_bid_seq: expectedBidSeq, p_command_id: commandId
    });
    if(error) return { ok:false, conflict:false, bidSeq:null, amount:null, deadlineAt:null, revision:null, error: mapLobbyRpcError(error) };
    return {
      ok:data.ok, conflict:!!data.conflict, bidSeq:data.bidSeq, amount:data.amount,
      deadlineAt:data.deadlineAt, revision:data.revision, error:data.error||null
    };
  }

  async finalizeIfExpired(auctionId, nominationId){
    const { data, error } = await this._client.rpc("finalize_if_expired", { p_auction_id: auctionId, p_nomination_id: nominationId });
    if(error) return { ok:false, alreadyFinalized:false, nominationId:null, outcome:null, winningSeatId:null, winningAmount:null, revision:null, error: mapLobbyRpcError(error) };
    return {
      ok:data.ok, alreadyFinalized:!!data.alreadyFinalized, nominationId:data.nominationId, outcome:data.outcome,
      winningSeatId:data.winningSeatId, winningAmount:data.winningAmount, revision:data.revision, error:data.error||null
    };
  }

  // ---------------- Live auction: settlement / purchase ledger / budget / roster (round alpha.5-r5-r1) ----------------
  // loadRosterRules/listAuctionPurchases: SELECT diretta filtrata da RLS (stessa scelta di
  // loadLiveRules()/listAuctionSeats()). configureRosterRules/settleNomination: thin RPC — ogni
  // scrittura passa da un'RPC SECURITY DEFINER (backend/migrations/
  // 0015_live_auction_settlement_budget_roster.sql), mai un INSERT/UPDATE/DELETE diretto su
  // auction_roster_rules/auction_purchases (RLS abilitata con SELECT owner-or-member ma ZERO policy di
  // scrittura per authenticated).

  async loadRosterRules(auctionId){
    const { data, error } = await this._client
      .from("auction_roster_rules")
      .select("auction_id, initial_budget_per_seat, prezzo_minimo, slot_por, slot_dif, slot_cen, slot_att, revision")
      .eq("auction_id", auctionId)
      .maybeSingle();
    if(error) throw new Error(mapLobbyRpcError(error));
    if(!data) return null;
    return {
      auctionId: data.auction_id, initialBudgetPerSeat: data.initial_budget_per_seat, prezzoMinimo: data.prezzo_minimo,
      slotPor: data.slot_por, slotDif: data.slot_dif, slotCen: data.slot_cen, slotAtt: data.slot_att, revision: data.revision
    };
  }

  async configureRosterRules(auctionId, initialBudgetPerSeat, expectedRevision, slotPOR = 3, slotDIF = 8, slotCEN = 8, slotATT = 6){
    const { data, error } = await this._client.rpc("configure_roster_rules", {
      p_auction_id: auctionId, p_initial_budget_per_seat: initialBudgetPerSeat, p_expected_revision: expectedRevision,
      p_slot_por: slotPOR, p_slot_dif: slotDIF, p_slot_cen: slotCEN, p_slot_att: slotATT
    });
    if(error) return { ok:false, conflict:false, revision:null, error: mapLobbyRpcError(error) };
    return { ok:data.ok, conflict:!!data.conflict, revision:data.revision, error:data.error||null };
  }

  async listAuctionPurchases(auctionId){
    const { data, error } = await this._client
      .from("auction_purchases")
      .select("id, nomination_id, seat_id, player_fantacalcio_id, amount, role_at_purchase, purchase_seq, resulting_revision, settled_by_user_id, created_at")
      .eq("auction_id", auctionId)
      .order("purchase_seq", { ascending:true });
    if(error) throw new Error(mapLobbyRpcError(error));
    return (data||[]).map(row => ({
      id: row.id, nominationId: row.nomination_id, seatId: row.seat_id, playerFantacalcioId: row.player_fantacalcio_id,
      amount: row.amount, roleAtPurchase: row.role_at_purchase, purchaseSeq: row.purchase_seq,
      resultingRevision: row.resulting_revision, settledByUserId: row.settled_by_user_id, createdAt: row.created_at
    }));
  }

  // listAuctionNominations — round A7-P1-6: SELECT diretta filtrata da RLS (stessa scelta già fatta
  // per listAuctionPurchases()/loadRosterRules() sopra — auction_nominations ha SELECT owner-or-member
  // già esistente da 0014 [auction_nominations_select_related], ZERO policy di scrittura per
  // authenticated: nominate_player()/finalize_if_expired()/settle_nomination() restano l'unica via di
  // scrittura, nessuna RPC/RLS/schema toccata da questo round). Proiezione esplicita e minima (mai
  // select('*')): esattamente i campi richiesti dal merge puro nomination_opened/player_sold/
  // player_unsold (A7-P1-6D §13) — command_id non è mai selezionato, mai esposto alla UI.
  // Ordinamento per nomination_seq crescente: contatore append-once per asta (0014/0015/0016,
  // coalesce(max(nomination_seq),0)+1), unico e mai ricalcolato — stesso principio di ordinamento
  // deterministico già usato da bid_seq/purchase_seq altrove in questo file.
  async listAuctionNominations(auctionId){
    const { data, error } = await this._client
      .from("auction_nominations")
      .select("id, nomination_seq, player_fantacalcio_id, nominating_seat_id, opening_price, status, final_amount, final_seat_id, opened_at, opened_at_revision, closed_at, closed_at_revision")
      .eq("auction_id", auctionId)
      .order("nomination_seq", { ascending:true });
    if(error) throw new Error(mapLobbyRpcError(error));
    return (data||[]).map(row => ({
      id: row.id, nominationSeq: row.nomination_seq, playerFantacalcioId: row.player_fantacalcio_id,
      nominatingSeatId: row.nominating_seat_id, openingPrice: row.opening_price, status: row.status,
      finalAmount: row.final_amount, finalSeatId: row.final_seat_id,
      openedAt: row.opened_at, openedAtRevision: row.opened_at_revision,
      closedAt: row.closed_at, closedAtRevision: row.closed_at_revision
    }));
  }

  // listBidsForNomination — round A7-P1-5: SELECT diretta filtrata da RLS (stessa scelta già fatta
  // per loadRosterRules()/listAuctionPurchases() sopra — auction_bids ha SELECT owner-or-member,
  // ZERO policy di scrittura per authenticated, place_bid() resta l'unica via di scrittura). Ambito
  // auction-scoped (amendment 4): filtrata sia da auction_id sia da nomination_id, mai solo
  // quest'ultima da sola — coerente con la coppia di colonne che la FK composita di auction_bids
  // referenzia già (auction_id, nomination_id) -> auction_nominations(auction_id, id). Proiezione di
  // colonne esplicita e minima (mai select('*')): submitted_by_user_id e command_id NON sono
  // selezionati — mai esposti alla UI, che mostra solo l'identità di squadra/seat (§25 del design).
  async listBidsForNomination(auctionId, nominationId){
    const { data, error } = await this._client
      .from("auction_bids")
      .select("id, nomination_id, seat_id, amount, bid_seq, created_at")
      .eq("auction_id", auctionId)
      .eq("nomination_id", nominationId)
      .order("bid_seq", { ascending:true });
    if(error) throw new Error(mapLobbyRpcError(error));
    return (data||[]).map(row => ({
      id: row.id, auctionId, nominationId: row.nomination_id, seatId: row.seat_id,
      amount: row.amount, bidSeq: row.bid_seq, createdAt: row.created_at
    }));
  }

  async settleNomination(auctionId, nominationId){
    const { data, error } = await this._client.rpc("settle_nomination", { p_auction_id: auctionId, p_nomination_id: nominationId });
    if(error) return { ok:false, alreadySettled:false, purchaseId:null, seatId:null, playerFantacalcioId:null, amount:null, revision:null, error: mapLobbyRpcError(error) };
    return {
      ok:data.ok, alreadySettled:!!data.alreadySettled, purchaseId:data.purchaseId, seatId:data.seatId,
      playerFantacalcioId:data.playerFantacalcioId, amount:data.amount, revision:data.revision, error:data.error||null
    };
  }
}
